import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { Modal } from 'antd';

import { toggleWorkspaceTagsModal } from '../../../../actions';
import { addWorkspaceTag, getWorkspaceTags, removeWorkspaceTag, updateWorkspaceTag } from '../../../../actions/WorkspaceActions';
import { getRandomColor, getTagColor } from '../../../../js/helper';
import { COLOUR_PALETTE } from '../../../../js/constants';

const TAG_SWATCHES = COLOUR_PALETTE;

const ColorPickerPortal = ({ anchorRef, current, onSelect, onClose }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: r.left - 4 });
  }, [anchorRef]);

  useEffect(() => {
    const handler = (e) => { if (!anchorRef.current?.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, anchorRef]);

  return createPortal(
    <div
      className="p-2 rounded-xl shadow-2xl"
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, background: 'white', border: '1px solid rgba(99,102,241,0.15)', width: 164 }}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="grid grid-cols-5 gap-1.5">
        {TAG_SWATCHES.map(c => (
          <button
            key={c}
            type="button"
            onClick={() => { onSelect(c); onClose(); }}
            className="w-6 h-6 rounded-full transition-transform hover:scale-110"
            style={{ background: c, outline: current === c ? `2px solid ${c}` : 'none', outlineOffset: 2 }}
          />
        ))}
      </div>
    </div>,
    document.body,
  );
};

const WorkspaceTagsModal = ({ isModalOpen, tags, workspace }) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  const [workspaceTags, setWorkspaceTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [newTagColor, setNewTagColor] = useState(null);
  const [search, setSearch] = useState('');
  const [addFocused, setAddFocused] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [colorPickerTagId, setColorPickerTagId] = useState(null);
  const [newTagColorPickerOpen, setNewTagColorPickerOpen] = useState(false);
  const colorDotRefs = useRef({});
  const newTagDotRef = useRef(null);

  useEffect(() => { setWorkspaceTags(tags || []); }, [tags]);

  const closeModal = () => dispatch(toggleWorkspaceTagsModal(false));

  const handleAdd = () => {
    const trimmed = tagInput.trim();
    if (!trimmed) return;
    const color = newTagColor || getRandomColor(workspaceTags.length);
    dispatch(addWorkspaceTag(
      { tag: trimmed, workspace, metadata: { color } },
      () => {
        dispatch(getWorkspaceTags());
        setTagInput('');
        setNewTagColor(null);
        inputRef.current?.focus();
      }
    ));
  };

  const handleDelete = (tag) => {
    dispatch(removeWorkspaceTag(tag._id));
    setWorkspaceTags(prev => prev.filter(t => t._id !== tag._id));
  };

  const handleColorChange = (tag, color) => {
    setWorkspaceTags(prev => prev.map(t => t._id === tag._id ? { ...t, metadata: { ...t.metadata, color } } : t));
    dispatch(updateWorkspaceTag(tag._id, { metadata: { ...tag.metadata, color } }));
  };

  const filtered = workspaceTags.filter(t => t.tag?.toLowerCase().includes(search.toLowerCase()));
  const displayColor = newTagColor || getRandomColor(workspaceTags.length);

  return (
    <Modal
      open={isModalOpen}
      onCancel={closeModal}
      width={540}
      centered
      footer={null}
      closeIcon={null}
      styles={{
        content: { padding: 0, borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(99,102,241,0.12)', boxShadow: '0 16px 48px rgba(99,102,241,0.14)' },
        mask: { backdropFilter: 'blur(2px)', background: 'rgba(15,15,25,0.35)' },
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99,102,241,0.1)' }}>
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.18)' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(99,102,241,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
              <line x1="7" y1="7" x2="7.01" y2="7"/>
            </svg>
          </div>
          <div>
            <p className="text-[14px] font-semibold" style={{ color: 'rgba(15,15,25,0.88)', margin: 0 }}>Workspace Tags</p>
            <p className="text-[11.5px]" style={{ color: 'rgba(15,15,25,0.4)', margin: 0 }}>{workspaceTags.length} tag{workspaceTags.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={closeModal}
          className="flex items-center justify-center w-7 h-7 rounded-lg transition-all"
          style={{ color: 'rgba(15,15,25,0.4)', border: 'none', background: 'transparent', cursor: 'pointer' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; e.currentTarget.style.color = 'rgba(99,102,241,0.8)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(15,15,25,0.4)'; }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="px-6 py-4" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Search */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(99,102,241,0.45)" strokeWidth="2.2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Search tags…"
            style={{
              width: '100%', height: 36, paddingLeft: 30, paddingRight: 10,
              fontSize: 13, borderRadius: 9, outline: 'none',
              border: searchFocused ? '1.5px solid rgba(99,102,241,0.5)' : '1.5px solid rgba(99,102,241,0.18)',
              background: searchFocused ? '#fff' : 'rgba(99,102,241,0.02)',
              color: 'rgba(15,15,25,0.8)',
              boxShadow: searchFocused ? '0 0 0 3px rgba(99,102,241,0.08)' : 'none',
              transition: 'all 0.15s',
            }}
          />
        </div>

        {/* Tag cloud */}
        <div className="overflow-y-auto" style={{ minHeight: 120, maxHeight: 300, padding: '2px 0' }}>
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(99,102,241,0.25)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                <line x1="7" y1="7" x2="7.01" y2="7"/>
              </svg>
              <p style={{ fontSize: 12.5, color: 'rgba(15,15,25,0.35)', margin: 0 }}>
                {search ? 'No matching tags' : 'No tags yet — add one below'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {filtered.map((tag, i) => {
                const bg = tag?.metadata?.color ? getTagColor(tag.metadata.color) : getRandomColor(i);
                if (!colorDotRefs.current[tag._id]) colorDotRefs.current[tag._id] = React.createRef();
                const dotRef = colorDotRefs.current[tag._id];
                return (
                  <div
                    key={tag._id || i}
                    className="inline-flex items-center gap-1.5 group"
                    style={{ height: 30, paddingLeft: 6, paddingRight: 6, borderRadius: 8, background: bg, fontSize: 12.5, fontWeight: 500, color: '#fff' }}
                  >
                    {/* Color dot — click to change color */}
                    <button
                      ref={dotRef}
                      type="button"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => setColorPickerTagId(id => id === tag._id ? null : tag._id)}
                      className="w-3.5 h-3.5 rounded-full flex-shrink-0 hover:scale-110 transition-transform"
                      style={{ background: 'rgba(255,255,255,0.35)', border: '1.5px solid rgba(255,255,255,0.6)' }}
                      title="Change color"
                    />
                    {colorPickerTagId === tag._id && (
                      <ColorPickerPortal
                        anchorRef={dotRef}
                        current={bg}
                        onSelect={(c) => handleColorChange(tag, c)}
                        onClose={() => setColorPickerTagId(null)}
                      />
                    )}
                    <span>{tag.tag}</span>
                    <button
                      onClick={() => handleDelete(tag)}
                      style={{
                        width: 18, height: 18, borderRadius: 5, border: 'none',
                        background: 'rgba(0,0,0,0)', color: 'rgba(255,255,255,0.7)',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, transition: 'background 0.12s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.22)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0)'}
                    >
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Add new tag section */}
        <div style={{ borderTop: '1px solid rgba(99,102,241,0.08)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ margin: 0, fontSize: 11.5, fontWeight: 600, color: 'rgba(15,15,25,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Add new tag</p>
          {/* Color row */}
          <div className="flex items-center gap-2.5">
            <span style={{ fontSize: 12.5, color: 'rgba(15,15,25,0.5)', flexShrink: 0 }}>Color</span>
            <button
              ref={newTagDotRef}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => setNewTagColorPickerOpen(p => !p)}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              style={{
                height: 30, paddingLeft: 8, paddingRight: 10, borderRadius: 8,
                border: `1.5px solid ${newTagColorPickerOpen ? 'rgba(99,102,241,0.4)' : 'rgba(99,102,241,0.18)'}`,
                background: newTagColorPickerOpen ? 'rgba(99,102,241,0.04)' : 'transparent',
                cursor: 'pointer', boxShadow: newTagColorPickerOpen ? '0 0 0 3px rgba(99,102,241,0.08)' : 'none',
                transition: 'all 0.15s',
              }}
              title="Pick color"
            >
              <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: displayColor }} />
              <span style={{ fontSize: 12, color: 'rgba(15,15,25,0.55)', fontFamily: 'monospace' }}>{displayColor}</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(15,15,25,0.35)" strokeWidth="2.5" strokeLinecap="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {newTagColorPickerOpen && (
              <ColorPickerPortal
                anchorRef={newTagDotRef}
                current={newTagColor}
                onSelect={(c) => setNewTagColor(c)}
                onClose={() => setNewTagColorPickerOpen(false)}
              />
            )}
          </div>
          {/* Name + Add button row */}
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
              onFocus={() => setAddFocused(true)}
              onBlur={() => setAddFocused(false)}
              placeholder="Tag name…"
              style={{
                flex: 1, height: 36, padding: '0 12px',
                fontSize: 13, borderRadius: 9, outline: 'none',
                border: addFocused ? '1.5px solid rgba(99,102,241,0.5)' : '1.5px solid rgba(99,102,241,0.18)',
                background: addFocused ? '#fff' : 'rgba(99,102,241,0.02)',
                color: 'rgba(15,15,25,0.8)',
                boxShadow: addFocused ? '0 0 0 3px rgba(99,102,241,0.08)' : 'none',
                transition: 'all 0.15s',
              }}
            />
            <button
              onClick={handleAdd}
              disabled={!tagInput.trim()}
              style={{
                height: 36, padding: '0 16px', borderRadius: 9,
                fontSize: 13, fontWeight: 600, cursor: tagInput.trim() ? 'pointer' : 'not-allowed',
                border: '1.5px solid', transition: 'all 0.15s', flexShrink: 0,
                ...(tagInput.trim()
                  ? { background: '#4f46e5', borderColor: '#4f46e5', color: '#fff', boxShadow: '0 2px 8px rgba(79,70,229,0.28)' }
                  : { background: 'rgba(99,102,241,0.05)', borderColor: 'rgba(99,102,241,0.12)', color: 'rgba(99,102,241,0.35)' }
                ),
              }}
            >
              + Add Tag
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-6 py-3" style={{ borderTop: '1px solid rgba(99,102,241,0.08)' }}>
        <span style={{ fontSize: 12, color: 'rgba(15,15,25,0.35)' }}>
          {search && filtered.length < workspaceTags.length
            ? `${filtered.length} of ${workspaceTags.length} tags`
            : `${workspaceTags.length} tag${workspaceTags.length !== 1 ? 's' : ''} total`}
        </span>
        <button
          onClick={closeModal}
          style={{
            height: 32, padding: '0 16px', borderRadius: 8,
            fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
            background: 'rgba(99,102,241,0.07)', border: '1.5px solid rgba(99,102,241,0.18)',
            color: 'rgba(99,102,241,0.8)', transition: 'all 0.12s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.12)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.07)'; }}
        >
          Done
        </button>
      </div>
    </Modal>
  );
};

export default WorkspaceTagsModal;
